/**
 * IndexedDB-backed react-query persister.
 *
 * Store name : gha-query-cache  (per-origin, consistent with DevTools path)
 * TTL        : 15 min
 * Buster     : "v2.6"  — increment on breaking query schema changes
 *
 * Whitelist  : only "runs", "runs-previous" and "workflows" queries are
 *              persisted (success state only). rate_limit / jobs / timing
 *              are intentionally excluded.
 *
 * Circular-import safety: this module MUST NOT import anything from
 * src/lib/auth/session.ts (only the reverse direction is allowed).
 */

import type { OmitKeyof } from "@tanstack/react-query";
import type { Query } from "@tanstack/react-query";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { clear, createStore, get, set, del } from "idb-keyval";
import { clearDayCache } from "@/lib/persist/day-cache";

// ---------------------------------------------------------------------------
// Internal IndexedDB store (db: "gha-cache", store: "gha-query-cache")
// ---------------------------------------------------------------------------

// Some browsers / extensions throw synchronously on `createStore` when
// IndexedDB is blocked (strict tracker blockers, sandboxed iframes). We
// guard to avoid crashing the whole SPA at import time — a null store
// downgrades the persister to a silent no-op.
type IdbStore = ReturnType<typeof createStore>;
let idbStore: IdbStore | null = null;
try {
  idbStore = createStore("gha-cache", "gha-query-cache");
} catch (err) {
  if (import.meta.env.DEV) {
    console.warn("[persister] IndexedDB unavailable, cache disabled", err);
  }
}

/**
 * Adapter that exposes the idb-keyval custom store as the AsyncStorage
 * interface expected by createAsyncStoragePersister. If IndexedDB failed to
 * initialise, the adapter gracefully no-ops instead of throwing.
 */
const idbStorage = {
  getItem: async (key: string): Promise<string | undefined | null> => {
    if (!idbStore) return null;
    return get<string>(key, idbStore);
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (!idbStore) return;
    await set(key, value, idbStore);
  },
  removeItem: async (key: string): Promise<void> => {
    if (!idbStore) return;
    await del(key, idbStore);
  },
};

// ---------------------------------------------------------------------------
// Persister instance
// ---------------------------------------------------------------------------

function createQueryPersister(): ReturnType<typeof createAsyncStoragePersister> {
  return createAsyncStoragePersister({
    storage: idbStorage,
    key: "gha-query-cache",
  });
}

// ---------------------------------------------------------------------------
// Cache wipe — called by wipeSession() on sign-out / 401
// ---------------------------------------------------------------------------

/**
 * Clears the entire persisted cache from IndexedDB.
 * Safe to call even if no cache exists or IndexedDB is unavailable.
 * Swallows all errors silently so quota / extension blockers never crash the UI.
 */
export async function clearPersistedCache(): Promise<void> {
  // Wipe both caches in parallel: react-query hydration blob + per-day slices.
  const queryClear = idbStore
    ? clear(idbStore).catch((err) => {
        if (import.meta.env.DEV) {
          console.warn("[persister] query-cache clear failed", err);
        }
      })
    : Promise.resolve();
  await Promise.all([queryClear, clearDayCache()]);
}

// ---------------------------------------------------------------------------
// Dehydrate filter
// ---------------------------------------------------------------------------

const PERSISTED_KEYS = new Set(["runs", "runs-previous", "workflows"]);

function shouldDehydrateQuery(query: Query): boolean {
  const firstKey = query.queryKey[0];
  // Fail-closed if the first key isn't a string (future non-string queryKey
  // shapes should explicitly opt in via the whitelist — never silently pass).
  if (typeof firstKey !== "string") return false;
  if (!PERSISTED_KEYS.has(firstKey)) return false;
  return query.state.status === "success";
}

// ---------------------------------------------------------------------------
// Exported persist options — passed directly to <PersistQueryClientProvider>
// ---------------------------------------------------------------------------

type PersistOptions = OmitKeyof<PersistQueryClientOptions, "queryClient">;

// TTL can be overridden at bundle time via BUN_PUBLIC_CACHE_TTL_MINUTES for
// local development (e.g. set to 1440 for a day-long cache). Invalid or
// missing values fall back to the 15-minute default. Bun substitutes the
// direct `process.env.X` access at build — do not use optional chaining.
const DEFAULT_TTL_MIN = 15;
const rawTtl = process.env.BUN_PUBLIC_CACHE_TTL_MINUTES;
const parsedTtl = rawTtl ? Number.parseInt(rawTtl, 10) : NaN;
const ttlMinutes = Number.isFinite(parsedTtl) && parsedTtl > 0 ? parsedTtl : DEFAULT_TTL_MIN;

export const persistOptions: PersistOptions = {
  persister: createQueryPersister(),
  maxAge: ttlMinutes * 60 * 1000,
  buster: "v2.6",
  dehydrateOptions: {
    shouldDehydrateQuery,
  },
};
